import { ChartNoteCard, docFromPlainText, isDocEmpty } from '@odo/shared-ui';
import type { CalendarAppointment } from '@odo/shared-ui';
import { usePatientNotes } from '../../../hooks/usePatientNotes';

function NoteSkeleton() {
    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 space-y-2">
            <div className="h-3.5 w-1/3 rounded bg-gray-200 animate-pulse" />
            <div className="h-3 w-full rounded bg-gray-200 animate-pulse" />
            <div className="h-3 w-2/3 rounded bg-gray-200 animate-pulse" />
        </div>
    );
}

/** Resolves `appointment.noteId` against the patient's note list (there's no
 *  fetch-by-id endpoint). */
function LinkedNote({ appointment }: { appointment: CalendarAppointment }) {
    const shouldFetch = !!appointment.noteId;
    const { notes, archivedNotes, isLoading } = usePatientNotes(
        shouldFetch ? appointment.patientId : undefined
    );

    if (!shouldFetch) return null;
    if (isLoading) return <NoteSkeleton />;

    const note = [...notes, ...archivedNotes].find((n) => n.id === appointment.noteId);
    if (!note) {
        return <p className="text-[13px] text-gray-400 italic">Chart note unavailable.</p>;
    }

    const doc =
        note.bodyDoc && !isDocEmpty(note.bodyDoc) ? note.bodyDoc : docFromPlainText(note.body);

    return <ChartNoteCard title={note.title} doc={doc} />;
}

interface AppointmentChartNoteSectionProps {
    appointment: CalendarAppointment;
}

export function AppointmentChartNoteSection({ appointment }: AppointmentChartNoteSectionProps) {
    return (
        <div>
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                Chart note
            </span>
            <div className="mt-1.5">
                {appointment.hasNote ? (
                    <LinkedNote appointment={appointment} />
                ) : (
                    <p className="text-[13px] text-gray-400 italic">No chart note.</p>
                )}
            </div>
        </div>
    );
}
